/**
 * MXlab — Long text fold ("Lire la suite").
 *
 * The full text is always in the HTML: folding is only an inline `max-height` with
 * `overflow: hidden`. The height is animated between two px values (scrollHeight and the
 * folded height), never from `none` or a `calc()`, which do not interpolate. Both end states
 * are concrete px, so the result is right even when the transition does not play.
 */
class MxLongform extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-mx-lf-body]');
    this.toggle = this.querySelector('[data-mx-lf-toggle]');
    this.moreLabel = this.querySelector('[data-mx-lf-more]');
    this.lessLabel = this.querySelector('[data-mx-lf-less]');
    if (!this.toggle || !this.body) return;

    this.onClick = () => this.toggleExpanded();
    this.toggle.addEventListener('click', this.onClick);

    // The Liquid sets the initial height as a calc() to avoid a flash. Converted to px
    // without animation, so the first transition starts from a px value.
    if (this.hasAttribute('data-collapse')) {
      this.setInstant(this.collapsedPx() + 'px');
    }

    // Called directly: requestAnimationFrame never runs in a background tab.
    this.pruneIfShort();
  }

  /** Sets max-height without animation: transition off for one reflow. */
  setInstant(value) {
    const prev = this.body.style.transition;
    this.body.style.transition = 'none';
    this.body.style.maxHeight = value;
    void this.body.offsetHeight;
    this.body.style.transition = prev;
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onClick);
  }

  /** Folded height: --mx-lf-lines times the body's actual line height. */
  collapsedPx() {
    const lines = parseFloat(getComputedStyle(this).getPropertyValue('--mx-lf-lines'));
    const lh = parseFloat(getComputedStyle(this.body).lineHeight) || 0;
    return Math.round(lines * lh);
  }

  /** Text that fits in the folded height: no fold, no gradient, no button. */
  pruneIfShort() {
    if (!this.hasAttribute('data-collapse')) return;
    // 2px margin for rounding.
    if (this.body.scrollHeight <= this.body.clientHeight + 2) {
      this.removeAttribute('data-collapse');
      this.body.style.maxHeight = '';
      this.toggle.hidden = true;
    }
  }

  toggleExpanded() {
    if (this.hasAttribute('data-expanded')) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  // scrollHeight is the full height even while overflow is hidden.
  expand() {
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.moreLabel) this.moreLabel.hidden = true;
    if (this.lessLabel) this.lessLabel.hidden = false;
    this.setAttribute('data-expanded', '');
    this.body.style.maxHeight = this.body.scrollHeight + 'px';
  }

  collapse() {
    this.toggle.setAttribute('aria-expanded', 'false');
    if (this.moreLabel) this.moreLabel.hidden = false;
    if (this.lessLabel) this.lessLabel.hidden = true;
    this.removeAttribute('data-expanded');
    this.body.style.maxHeight = this.collapsedPx() + 'px';
  }
}

if (!customElements.get('mx-longform')) {
  customElements.define('mx-longform', MxLongform);
}
